/**
 * Content-site shared types (A7Y-4469 — content site programme, phase 1a).
 *
 * Single source of truth for the section-agnostic content domain shared by the
 * blog and (from A7Y-4470 onward) docs CMS: lifecycle status/reasons, the
 * publish-time render contract (`@a7y/content-render`'s `RenderedContent`),
 * the author registry, docs pages/nav/redirects, reader feedback, and the KV
 * key space `@a7y/content-render` and `a7y-api`'s `services/content/*` both
 * consume.
 */

import { stripBulletinSidecar } from './weekly-bulletin.js';

// ---------------------------------------------------------------------------
// Lifecycle
// ---------------------------------------------------------------------------

/** The two sections the content service currently (or will) drive. */
export type ContentSection = 'blog' | 'docs';

/**
 * One shared page-status vocabulary across sections. Replaces the blog-only
 * `BlogStatus` — `archived` is new (A7Y-4469 D8/D12/D14).
 */
export type ContentStatus = 'draft' | 'scheduled' | 'published' | 'archived';

/** Reasons an immutable revision snapshot was written. */
export type ContentRevisionReason = 'publish' | 'restore' | 'unpublish' | 'archive';

// ---------------------------------------------------------------------------
// Render contract (@a7y/content-render's public return shape)
// ---------------------------------------------------------------------------

export interface ContentHeading {
  id: string;
  level: 1 | 2 | 3 | 4 | 5 | 6;
  text: string;
}

/** Nested table-of-contents entry: H2 entries with nested H3 children. */
export interface ContentTocItem {
  id: string;
  text: string;
  level: 2 | 3;
  children: ContentTocItem[];
}

/**
 * The publish-time render of a page body. Stored only on the KV read-model
 * record (`BlogPostKvRecord.rendered`) — never in Firestore, which is why
 * every field here must round-trip through JSON with no loss.
 */
export interface RenderedContent {
  html: string;
  toc: ContentTocItem[];
  headings: ContentHeading[];
  /** `hast-util-to-text` output with whitespace collapsed. */
  text: string;
  /** `Math.max(1, Math.round(words / 200))`. */
  readingMinutes: number;
  rendererVersion: number;
}

// ---------------------------------------------------------------------------
// Section-agnostic service base types (A7Y-4469 D25 — Karen round-1 F7):
// `content_service.ts` is written entirely against these, never a section's
// own document/revision type.
// ---------------------------------------------------------------------------

/**
 * The fields every content document carries, regardless of section. A
 * section's own doc type (`BlogPost`, `DocPage`) extends this.
 */
export interface ContentDocBase {
  id: string;
  slug: string;
  title: string;
  status: ContentStatus;
  body: string;
  authorIds?: string[];
  goLiveAt?: string;
  publishedAt?: string;
  /**
   * The slug this document is served at RIGHT NOW, set only while `status`
   * is `"published"` (D12, revised). `null` means "not live" — distinct
   * from `undefined`, which means "never stamped" (a legacy document).
   */
  publishedSlug?: string | null;
  publishedSummary?: unknown;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
  /**
   * A7Y-4474 D15 — a status change requested but not yet applied (e.g.
   * "Queue in Publishing"). Set without bumping `updatedAt`; cleared by
   * every lifecycle merge that actually changes `status` (publish,
   * schedule, unpublish, archive). `null` (not absent) once cleared, so a
   * leaf-mask merge always overwrites a stale value.
   */
  pendingStatus?: ContentPendingStatus | null;
}

/** The fields every immutable revision snapshot carries, regardless of section. */
export interface ContentRevisionBase<TDoc> {
  id: string;
  snapshot: TDoc;
  author: string;
  createdAt: string;
  reason: ContentRevisionReason;
}

/**
 * The slug a document is LIVE at right now, or `null` if it is not live
 * (A7Y-4469 D12, revised — Karen round-1 F1). A document's live key belongs
 * to it and only it: every eviction, tombstone and old-key deletion targets
 * `liveSlugOf`, never a remembered `publishedSummary.slug` that nothing
 * clears when the document stops being live.
 */
export function liveSlugOf(
  doc: Pick<ContentDocBase, "status" | "slug" | "publishedSlug">,
): string | null {
  return doc.status === "published" ? doc.publishedSlug ?? doc.slug : null;
}

// ---------------------------------------------------------------------------
// Author registry
// ---------------------------------------------------------------------------

export interface Author {
  id: string;
  name: string;
  avatarUrl: string;
  url?: string;
}

export interface AuthorCreateRequest {
  name: string;
  avatarUrl: string;
  url?: string;
}

export type AuthorUpdateRequest = Partial<AuthorCreateRequest>;

export interface AuthorListResponse {
  authors: Author[];
}

export interface AuthorMutationResponse {
  success: true;
  data: Author;
}

// ---------------------------------------------------------------------------
// Docs (A7Y-4470 registers the `docs` section)
// ---------------------------------------------------------------------------

export interface DocPage extends ContentDocBase {
  description: string;
  navLabel?: string | null;
  unlisted: boolean;
  publishedSummary?: DocsIndexEntry | null;
  /** A7Y-4480 — set when an agent created this page as a draft for a ticket. */
  agentOrigin?: ContentAgentOrigin;
}

// ---------------------------------------------------------------------------
// Agent docs drafts and suggestions (A7Y-4480)
// ---------------------------------------------------------------------------

/** Linear ticket ids an agent may attach a docs draft or suggestion to. */
export const CONTENT_AGENT_TICKET_REGEX = /^A7Y-\d+$/;

/** Where an agent-created draft came from, and whether its feature is live. */
export interface ContentAgentOrigin {
  ticketId: string;
  commit?: string;
  /** Stamped by the production deploy once the ticket's code is live. */
  featureLiveAt?: string;
}

export type DocsSuggestionStatus = 'pending' | 'applied' | 'dismissed';

/** The fields a suggestion may propose for an existing docs page. */
export interface DocsSuggestionProposal {
  body: string;
  title?: string;
  description?: string;
  navLabel?: string | null;
}

/**
 * A7Y-4480 — an agent's proposed change to an existing docs page. Stored in
 * its own collection (`DocsSuggestions`) so it never touches the page's
 * working copy; a person applies it into the working copy and publishes.
 */
export interface DocsSuggestion {
  id: string;
  pageId: string;
  pageSlug: string;
  status: DocsSuggestionStatus;
  proposed: DocsSuggestionProposal;
  /** The page's `updatedAt` when the suggestion was made (stale-apply warning). */
  baseUpdatedAt: string;
  ticketId: string;
  commit?: string;
  createdAt: string;
  createdBy: string;
  featureLiveAt?: string;
  resolvedAt?: string;
  resolvedBy?: string;
  env?: string;
}

/** `POST /admin/docs/:id/suggestions` request body. */
export interface DocsSuggestionCreateRequest extends DocsSuggestionProposal {
  ticketId: string;
  commit?: string;
}

/** `POST /admin/docs/suggestions/tickets` request body. */
export interface DocsSuggestionTicketsRequest {
  ticketIds: string[];
  /** `feature-live`: stamp the tickets' pending suggestions and agent drafts. `discard-cancelled`: dismiss their pending suggestions. */
  action: 'feature-live' | 'discard-cancelled';
}

export interface DocsSuggestionTicketsResult {
  suggestions: number;
  drafts: number;
}

export interface DocPageRevision extends ContentRevisionBase<DocPage> {
  pageId: string;
}

/** One row of the read-model docs index (`docs:index`), sorted by slug. */
export interface DocsIndexEntry {
  id: string;
  slug: string;
  title: string;
  description: string;
  navLabel: string | null;
  unlisted: boolean;
  updatedAt: string;
  publishedAt: string | null;
}

/** The `docs:index` KV value — every live docs page, listed and unlisted. */
export type DocsIndex = DocsIndexEntry[];

/** Project a live `DocPage` into its `docs:index` row. */
export function docsIndexEntry(doc: DocPage): DocsIndexEntry {
  return {
    id: doc.id,
    slug: doc.publishedSlug ?? doc.slug,
    title: doc.title,
    description: doc.description,
    navLabel: doc.navLabel ?? null,
    unlisted: doc.unlisted,
    updatedAt: doc.updatedAt,
    publishedAt: doc.publishedAt ?? null,
  };
}

/** Build the sorted-by-slug `docs:index` value from a set of entries. */
export function buildDocsIndex(entries: DocsIndexEntry[]): DocsIndex {
  return [...entries].sort((a, b) => a.slug.localeCompare(b.slug));
}

/** The `docs:page:<slug>` KV record: the live page, plus its publish-time render. */
export type DocPageKvRecord = DocPage & { rendered?: RenderedContent };
export type DocPageKvValue = DocPageKvRecord | ContentTombstone;

// ---------------------------------------------------------------------------
// Docs nav — Firestore config (`DocsNav/<env>`, keyed by page id) and the
// resolved shape (`docs:nav` KV, keyed by slug) `resolveDocsNav` derives from
// it plus the live index.
// ---------------------------------------------------------------------------

export type DocsNavConfigLink =
  | { kind: 'page'; pageId: string }
  | { kind: 'external'; href: string; label: string; pill?: string };

export type DocsNavConfigEntry =
  | DocsNavConfigLink
  | { kind: 'group'; label: string; items: DocsNavConfigLink[] };

export interface DocsNavConfig {
  entries: DocsNavConfigEntry[];
  intro: string;
  introHtml: string;
  updatedAt: string;
  updatedBy: string;
}

export type DocsNavLink =
  | { kind: 'page'; slug: string; label: string }
  | { kind: 'external'; href: string; label: string; pill?: string };

export type DocsNavEntry =
  | DocsNavLink
  | { kind: 'group'; label: string; items: DocsNavLink[] };

export interface DocsNav {
  entries: DocsNavEntry[];
  introHtml: string;
}

/**
 * Resolve the Firestore nav config against the live docs index: drops any
 * `page` entry whose `pageId` is absent from `index` or whose page is
 * `unlisted`, labels a resolved page `navLabel ?? title`, and drops any
 * `group` left with no entries.
 */
export function resolveDocsNav(
  config: Pick<DocsNavConfig, 'entries' | 'introHtml'>,
  index: DocsIndex,
): DocsNav {
  const byId = new Map(index.map((entry) => [entry.id, entry]));

  const resolveLink = (link: DocsNavConfigLink): DocsNavLink | null => {
    if (link.kind === 'external') return link;
    const entry = byId.get(link.pageId);
    if (!entry || entry.unlisted) return null;
    return { kind: 'page', slug: entry.slug, label: entry.navLabel ?? entry.title };
  };

  const entries: DocsNavEntry[] = [];
  for (const entry of config.entries) {
    if (entry.kind === 'group') {
      const items = entry.items.map(resolveLink).filter((item): item is DocsNavLink => item !== null);
      if (items.length === 0) continue;
      entries.push({ kind: 'group', label: entry.label, items });
      continue;
    }
    const resolved = resolveLink(entry);
    if (resolved) entries.push(resolved);
  }

  return { entries, introHtml: config.introHtml };
}

/** Flatten a resolved nav into its page entries, each carrying its group's label (or `null` at the top level). */
export function flattenDocsNavPages(
  nav: DocsNav,
): { slug: string; label: string; groupLabel: string | null }[] {
  const pages: { slug: string; label: string; groupLabel: string | null }[] = [];
  for (const entry of nav.entries) {
    if (entry.kind === 'page') {
      pages.push({ slug: entry.slug, label: entry.label, groupLabel: null });
    } else if (entry.kind === 'group') {
      for (const item of entry.items) {
        if (item.kind === 'page') {
          pages.push({ slug: item.slug, label: item.label, groupLabel: entry.label });
        }
      }
    }
  }
  return pages;
}

// ---------------------------------------------------------------------------
// Redirects (docs-only for now — A7Y-4470 D6/D15)
// ---------------------------------------------------------------------------

export interface ContentRedirect {
  id: string;
  section: ContentSection;
  from: string;
  to: string;
  reason: 'slug-rename' | 'manual';
  createdAt: string;
  createdBy: string;
}

/** A docs slug: one or more lowercase-kebab path segments, e.g. `audits/run-an-audit`. */
export const DOCS_SLUG_REGEX = /^[a-z0-9]+(?:-[a-z0-9]+)*(?:\/[a-z0-9]+(?:-[a-z0-9]+)*)*$/;

/** First path segment(s) a docs slug may never start with (reserved routes). */
export const DOCS_RESERVED_FIRST_SEGMENTS = ['preview'] as const;

/** `/docs/<slug>/`, or `/docs/` for the front page (`slug === ''`). */
export function docsPagePath(slug: string): string {
  return slug ? `/docs/${slug}/` : '/docs/';
}

/** `/docs/<slug>.md` — the Markdown-copy URL for a docs page. */
export function docsMarkdownPath(slug: string): string {
  return `/docs/${slug}.md`;
}

/**
 * The one Markdown-copy formatter both the Worker (`a7y-content`) and the
 * offline scanners (`scripts/lib/docs-mdx.cjs`'s `markdownCopy`) must produce
 * byte-identically — pinned by `fixtures/markdown-copy.golden.json` on both
 * sides (A7Y-4470 D10).
 */
export function buildMarkdownCopy(p: { title: string; description: string; body: string }): string {
  // A7Y-5609: a Weekly Bulletin draft's editor-only sidecar never reaches the public copy.
  return `---\ntitle: ${p.title}\ndescription: ${p.description}\n---\n\n${stripBulletinSidecar(p.body).trimEnd()}\n`;
}

// ---------------------------------------------------------------------------
// Docs publish/removal guard refusals (A7Y-4470 D5)
// ---------------------------------------------------------------------------

export type DocsGuardRefusal =
  | { kind: 'broken-link'; pageId: string; href: string; target: string }
  | { kind: 'docs-pages-slug'; pageId?: string; key: string; slug: string }
  | {
      kind: 'claim';
      pageId?: string;
      entryId: string;
      slug: string;
      textAnchor: string;
      occurrences: number;
    };

export interface DocsGuardRefusalResponse {
  success: false;
  error: string;
  refusals: DocsGuardRefusal[];
}

// ---------------------------------------------------------------------------
// Docs DTOs
// ---------------------------------------------------------------------------

export interface DocsListResponse {
  posts: DocPage[];
  total: number;
}

export interface DocsMutationResponse {
  success: true;
  data: DocPage;
}

export interface DocsCreateRequest {
  slug: string;
  title: string;
  description: string;
  navLabel?: string | null;
  unlisted?: boolean;
  body: string;
  authorIds?: string[];
  goLiveAt?: string;
  status?: 'draft' | 'scheduled';
}

export type DocsUpdateRequest = Partial<DocsCreateRequest>;

export interface DocsPublishRequest {
  redirectFromPreviousSlug?: boolean;
}

export interface DocsPublishBatchRequest {
  ids: string[];
}

export interface DocsPublishBatchResponse {
  success: true;
  data: { published: DocPage[] };
}

export interface DocsNavResponse {
  config: DocsNavConfig | null;
}

export interface DocsNavUpdateRequest {
  entries: DocsNavConfigEntry[];
  intro: string;
}

export interface DocsRedirectListResponse {
  redirects: ContentRedirect[];
}

export interface DocsRedirectCreateRequest {
  from: string;
  to: string;
}

export interface ContentFeedbackListResponse {
  feedback: ContentFeedback[];
}

export interface ContentFeedbackStatusRequest {
  status: ContentFeedbackStatus;
}

// ---------------------------------------------------------------------------
// Reader feedback
// ---------------------------------------------------------------------------

export type ContentFeedbackKind = 'question' | 'feedback' | 'vote';
export type ContentFeedbackStatus = 'new' | 'replied' | 'closed';

export interface ContentFeedbackPage {
  section: ContentSection;
  slug: string;
  url: string;
  title: string;
}

export interface ContentFeedback {
  id: string;
  kind: ContentFeedbackKind;
  helpful?: boolean;
  note: string;
  email?: string;
  contactOk: boolean;
  page: ContentFeedbackPage;
  createdAt: string;
  status: ContentFeedbackStatus;
}

/** A submission's note is trimmed then bounded to this many characters (A7Y-4474). */
export const CONTENT_FEEDBACK_NOTE_MAX = 2000;

/** `POST /apptonomy/content/feedback` request body (A7Y-4474 D13). */
export interface ContentFeedbackSubmitRequest {
  kind: ContentFeedbackKind;
  helpful?: boolean;
  note?: string;
  email?: string;
  contactOk: boolean;
  turnstileToken?: string;
  page: ContentFeedbackPage;
}

export type ContentFeedbackSubmitResponse =
  | { ok: true; id: string }
  | { ok: false; error: 'validation' | 'turnstile' | 'rate_limited' | 'internal'; fields?: Record<string, string> };

// ---------------------------------------------------------------------------
// Publishing queue / status log (A7Y-4474 D15)
// ---------------------------------------------------------------------------

/**
 * A status change requested (e.g. "Queue in Publishing") but not yet
 * applied — stored on the document without bumping `updatedAt`, cleared by
 * every lifecycle transition that changes `status`.
 */
export interface ContentPendingStatus {
  status: ContentStatus;
  goLiveAt?: string;
  requestedBy: string;
  requestedAt: string;
}

/** `POST /admin/blog/:id/pending-status` request body. `status: null` clears it. */
export interface ContentPendingStatusRequest {
  status: ContentStatus | null;
  goLiveAt?: string;
}

/** Why a document appears as a row in the Publishing queue. */
export type PublishingQueueReason = 'draft' | 'edits' | 'scheduled' | 'status-change' | 'suggestion';

export interface PublishingQueueRow {
  section: ContentSection;
  id: string;
  slug: string;
  title: string;
  status: ContentStatus;
  reasons: PublishingQueueReason[];
  everPublished: boolean;
  goLiveAt?: string;
  pendingStatus?: ContentPendingStatus;
  /** The raw actor identifier stamped on the document (an admin user id). */
  changedBy: string;
  /**
   * A7Y-4474 (Karen round-1) — a human-readable label for `changedBy`,
   * resolved from the admin user directory. `changedByResolved: false` means
   * the lookup missed (a deleted account, or no admin context — e.g. the
   * scheduled-publish cron) and `changedByName` is the same raw value as
   * `changedBy`: the admin UI must key its display on the flag, never treat
   * `changedByName` alone as a person's name.
   */
  changedByName: string;
  changedByResolved: boolean;
  changedAt: string;
  /** A7Y-4480 — pending agent suggestions on this page (reason `suggestion`). */
  suggestionCount?: number;
  /** A7Y-4480 — the ticket an agent draft or suggestion belongs to. */
  agentTicketIds?: string[];
  /** A7Y-4480 — true when every agent change on this row belongs to a ticket already in production. */
  featureLive?: boolean;
}

/**
 * One row of the 30-day status-change log (`ContentStatusLog` collection).
 * `actorName`/`actorResolved` are never stored — `recordStatusChange` writes
 * only `actor` (the raw id); `listRecentStatusChanges` (the sole reader)
 * always attaches them before returning, so every row this type reaches an
 * API response through carries both, but the STORED Firestore document does
 * not, hence optional here rather than a second DTO type.
 */
export interface ContentStatusLogEntry {
  id: string;
  section: ContentSection;
  docId: string;
  slug: string;
  title: string;
  from: ContentStatus;
  to: ContentStatus;
  /** The raw actor identifier stamped at write time (an admin user id). */
  actor: string;
  /** A7Y-4474 (Karen round-1) — see `PublishingQueueRow.changedByName`. */
  actorName?: string;
  actorResolved?: boolean;
  at: string;
}

/** `GET /admin/content/publishing` response. */
export interface PublishingQueueResponse {
  rows: PublishingQueueRow[];
  log: ContentStatusLogEntry[];
  windowDays: number;
}

// ---------------------------------------------------------------------------
// Author read model (A7Y-4474 D16)
// ---------------------------------------------------------------------------

/** KV key for the content-site author read model — an `Author[]` array. */
export const CONTENT_AUTHORS_KV_KEY = 'content:authors';

// ---------------------------------------------------------------------------
// Tombstones (A7Y-4469 D14) — an archived-but-ever-published page's KV page
// key is overwritten with this sentinel rather than deleted, so the OLD blog
// Worker (the retired Astro blog, pre-cutover) can distinguish "never existed" (404) from
// "existed, now retired" (also 404 until A7Y-4477, but never a 500).
// ---------------------------------------------------------------------------

export interface ContentTombstone {
  tombstone: true;
}

export const CONTENT_TOMBSTONE: ContentTombstone = { tombstone: true };

export function isContentTombstone(value: unknown): value is ContentTombstone {
  return (
    !!value && typeof value === 'object' && (value as { tombstone?: unknown }).tombstone === true
  );
}

// ---------------------------------------------------------------------------
// KV key space — single source of truth for every content-service key family.
// ---------------------------------------------------------------------------

/** Page-record key prefix per section: `blog:post:` / `docs:page:`. */
export const CONTENT_PAGE_KV_PREFIX: Record<ContentSection, string> = {
  blog: 'blog:post:',
  docs: 'docs:page:',
};

/** KV key for a single published docs page: `docs:page:<slug>`. */
export function docsPageKvKey(slug: string): string {
  return `${CONTENT_PAGE_KV_PREFIX.docs}${slug}`;
}

export const DOCS_NAV_KV_KEY = 'docs:nav';
export const DOCS_INDEX_KV_KEY = 'docs:index';

/** KV key for a redirect record. `path` must start with `/`. */
export function redirectKvKey(path: string): string {
  if (!path.startsWith('/')) {
    throw new Error(`redirectKvKey: path must start with "/" (got ${JSON.stringify(path)})`);
  }
  return `redirect:${path}`;
}

/** KV key for a section's generation token: `content:gen:<section>`. */
export function contentGenKvKey(section: ContentSection): string {
  return `content:gen:${section}`;
}

/**
 * How long after a generation bump the content Worker serves renders without
 * storing them in its edge cache (A7Y-4526). Workers KV reads are cached at
 * each edge for up to 60 s, so in the first minute after a publish a location
 * can already see the NEW generation but still read the OLD page record (or a
 * stale tombstone), and anything cached then stays pinned under the new
 * generation for the full cache TTL. Measured on staging 2026-09-27: an edit
 * took 124 s to appear and a restored page kept answering 410. Withholding the
 * store until every edge has caught up leaves visibility at KV's propagation
 * time and caches only settled content.
 */
export const CONTENT_GENERATION_SETTLE_MS = 120_000;

/** A new generation token: `<epoch ms>.<id>`, so the Worker can tell its age. */
export function newContentGeneration(now: number, id: string): string {
  return `${now}.${id}`;
}

/**
 * The bump time carried by a generation token, or `null` for a legacy
 * untimed token or none. Accepts the raw KV value, which is JSON-quoted.
 */
export function contentGenerationBumpedAt(generation: string | null | undefined): number | null {
  if (!generation) return null;
  const raw = generation.replace(/^"|"$/g, '');
  const match = raw.match(/^(\d{13})\./);
  return match ? Number(match[1]) : null;
}

/** True once a generation is older than the settle window (always, for a legacy token). */
export function contentGenerationSettled(
  generation: string | null | undefined,
  now: number,
  windowMs: number = CONTENT_GENERATION_SETTLE_MS
): boolean {
  const bumpedAt = contentGenerationBumpedAt(generation);
  return bumpedAt === null || now - bumpedAt >= windowMs;
}

// ---------------------------------------------------------------------------
// Render / re-render / migration request + report DTOs
// ---------------------------------------------------------------------------

export interface ContentRenderRequest {
  markdown: string;
}

export interface ContentRerenderRequest {
  section: ContentSection;
  force?: boolean;
  dryRun?: boolean;
  cursor?: string;
  limit?: number;
}

export interface ContentRerenderReport {
  section: ContentSection;
  scanned: number;
  rerendered: number;
  skippedCurrent: number;
  tombstones: number;
  failed: { key: string; error: string }[];
  cursor: string | null;
  generationBumped: boolean;
  dryRun: boolean;
}

/**
 * `POST /admin/content/reindex` (A7Y-4471 D8): one page of the KV walk
 * shared with `ContentRerenderReport`, re-projected into the section's
 * Algolia records. `pruned` is only non-zero on the final page of a
 * complete walk (`cursor === null`), when records whose page no longer has
 * a live KV record are removed.
 */
export interface ContentReindexReport {
  section: ContentSection;
  indexName: string;
  scanned: number;
  indexed: number;
  records: number;
  tombstones: number;
  skipped: { key: string; reason: string }[];
  failed: { key: string; error: string }[];
  pruned: number;
  cursor: string | null;
  dryRun: boolean;
}

export interface BlogAuthorMigrationReport {
  dryRun: boolean;
  scanned: number;
  createdAuthors: { id: string; name: string; url?: string }[];
  assigned: { postId: string; slug: string; authorIds: string[] }[];
  kvPatched: string[];
}

// ---------------------------------------------------------------------------
// Content keys (A7Y-4478) — the credential the content CLI sends
// ---------------------------------------------------------------------------

/**
 * `person` keys act as the person who minted them (one active key per person,
 * superadmin re-checked on every request); `automation` keys are drafts-only
 * unless minted with `canPublish` (A7Y-5078).
 */
export type ContentKeyKind = "person" | "automation";

/** The stored record. The key itself is never stored — only `secretHash`. */
export interface ContentKeyRecord {
  id: string;
  name: string;
  kind: ContentKeyKind;
  role: "super_admin" | "content_automation" | "content_publisher";
  /** An `automation` key minted with the publish right (A7Y-5078); absent means drafts-only. */
  canPublish?: boolean;
  /** The environment the key was minted in; it is refused in any other. */
  env: string;
  /** The person a `person` key acts as; the minting admin for `automation`. */
  ownerUserId: string;
  ownerEmail: string;
  ownerName: string;
  /** SHA-256 hex of the secret half of the key. */
  secretHash: string;
  /** The first characters of the key, for recognising it in a list. */
  prefix: string;
  createdAt: string;
  createdBy: string;
  revokedAt?: string;
  revokedBy?: string;
}

/** A record as the admin list returns it — never carries the hash. */
export type ContentKeySummary = Omit<ContentKeyRecord, "secretHash">;

export interface ContentKeyMintRequest {
  kind: ContentKeyKind;
  /** Required for `automation` keys; defaults to the person's name otherwise. */
  name?: string;
  /** `automation` only: the key may publish, schedule, unpublish and archive (A7Y-5078). */
  canPublish?: boolean;
}

export interface ContentKeyMintResponse {
  /** The key, shown once. */
  key: string;
  record: ContentKeySummary;
}

export interface ContentKeyListResponse {
  keys: ContentKeySummary[];
}

/** `GET /admin/content-keys/me` — who a key (or session) authenticates as. */
export interface ContentKeyWhoAmI {
  id: string;
  email: string;
  name: string;
  role: string;
  via: "content-key" | "session";
  keyId?: string;
  keyKind?: ContentKeyKind;
  /** True for a person key and a publish-capable automation key. */
  canPublish?: boolean;
}
